import { useNavigate } from 'react-router-dom';
import type { Book } from '@/features/library/services/booksApi';
import { bookStatusLabels, bookStatusTones } from '@/features/library/utils/status';
import { formatDayMonth } from '@/shared/utils/format';
import { LibraryIcon, cx } from '@/shared/ui';

export type BookCardProps = {
  book: Book;
};

/**
 * One book in the library grid: it stands in for all of its chapter videos, which
 * open from the book's own page. Drawn with the video card's classes so a book sits
 * in the grid as one of its cards, plus a count of its videos over the cover.
 */
export function BookCard({ book }: BookCardProps) {
  const navigate = useNavigate();
  const open = () => navigate(`/library/books/${book.id}`);
  const ready =
    book.completedCount === book.videoCount
      ? `${book.videoCount} ${book.videoCount === 1 ? 'video' : 'videos'}`
      : `${book.completedCount} of ${book.videoCount} ready`;

  return (
    <article
      className="lib-card lib-card--book"
      role="button"
      tabIndex={0}
      aria-label={`${book.title}, book, ${ready}`}
      onClick={open}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
    >
      <div className="lib-card__thumb">
        {book.thumbnailUrl ? (
          <img src={book.thumbnailUrl} alt="" loading="lazy" />
        ) : (
          <div className="lib-card__placeholder" aria-hidden />
        )}

        {book.status !== 'completed' ? (
          <span className={cx('lib-card__status', `lib-card__status--${bookStatusTones[book.status]}`)}>
            <span className="lib-card__status-dot" aria-hidden />
            {bookStatusLabels[book.status]}
          </span>
        ) : null}

        <span className="lib-card__book-count">
          <LibraryIcon size={14} />
          {ready}
        </span>
      </div>

      <div className="lib-card__body">
        <div className="lib-card__head">
          <h3 className="lib-card__title">{book.title}</h3>
        </div>
        <div className="lib-card__meta">
          <span className="lib-card__date">Created {formatDayMonth(book.createdAt)}</span>
        </div>
      </div>
    </article>
  );
}
